import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Add modern card styles to document
const cardStyles = `
  @keyframes cardHover {
    0% { transform: scale(1); }
    100% { transform: scale(1.005); }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  .card-modern {
    position: relative;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .dark .card-modern {
    background: rgba(30, 30, 30, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .card-modern::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, 
      rgba(var(--win11-accent-rgb), 0.1), 
      rgba(var(--win11-accent-rgb), 0.05), 
      transparent 50%);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    z-index: 0;
  }

  .card-modern:hover::before {
    opacity: 1;
  }

  .card-modern:hover {
    transform: translateY(-1px);
    box-shadow: 
      0 8px 20px -3px rgba(0, 0, 0, 0.08),
      0 3px 6px -4px rgba(0, 0, 0, 0.08),
      0 0 0 1px rgba(var(--win11-accent-rgb), 0.08);
  }

  .card-modern:focus-within {
    outline: none;
    box-shadow: 
      0 10px 25px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -4px rgba(0, 0, 0, 0.1),
      0 0 0 2px rgba(var(--win11-accent-rgb), 0.3);
  }

  .card-elevated {
    box-shadow: 
      0 1px 3px 0 rgba(0, 0, 0, 0.1),
      0 1px 2px -1px rgba(0, 0, 0, 0.1);
  }

  .card-elevated:hover {
    box-shadow: 
      0 12px 20px -5px rgba(0, 0, 0, 0.08),
      0 6px 8px -6px rgba(0, 0, 0, 0.08);
  }

  .card-interactive {
    cursor: pointer;
  }

  .card-interactive:hover {
    animation: cardHover 0.3s ease forwards;
    box-shadow: 
      0 8px 20px -3px rgba(0, 0, 0, 0.08),
      0 3px 6px -4px rgba(0, 0, 0, 0.08),
      0 0 0 1px rgba(var(--win11-accent-rgb), 0.08);
  }

  .card-interactive:active {
    transform: scale(0.995);
  }

  .card-glass {
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.18);
  }

  .dark .card-glass {
    background: rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .card-accent {
    background: linear-gradient(135deg, 
      rgba(var(--win11-accent-rgb), 0.05), 
      rgba(var(--win11-accent-rgb), 0.02));
    border: 1px solid rgba(var(--win11-accent-rgb), 0.2);
  }

  .card-accent:hover {
    background: linear-gradient(135deg, 
      rgba(var(--win11-accent-rgb), 0.08), 
      rgba(var(--win11-accent-rgb), 0.04));
    border-color: rgba(var(--win11-accent-rgb), 0.3);
  }

  .card-loading {
    position: relative;
    overflow: hidden;
  }

  .card-loading::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 255, 255, 0.4), 
      transparent);
    animation: shimmer 2s infinite;
    pointer-events: none;
    z-index: 1;
  }

  .dark .card-loading::after {
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 255, 255, 0.1), 
      transparent);
  }

  /* Ensure card content stays above decorative elements */
  .card-modern > * {
    position: relative;
    z-index: 2;
  }

  /* Disable animations for specific use cases */
  .card-no-animation,
  .card-no-animation:hover,
  .card-no-animation:active {
    transform: none !important;
    animation: none !important;
    transition: none !important;
    box-shadow: none !important;
  }

  .card-no-animation::before {
    opacity: 0 !important;
  }

  .card-no-animation:hover::before {
    opacity: 0 !important;
  }
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = cardStyles;
  document.head.appendChild(styleElement);
}

const cardVariants = cva(
  "card-modern rounded-xl",
  {
    variants: {
      variant: {
        default: "card-elevated",
        glass: "card-glass",
        accent: "card-accent",
        interactive: "card-interactive",
        loading: "card-loading",
      },
      size: {
        sm: "p-4",
        md: "p-6",
        lg: "p-8",
        xl: "p-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof cardVariants> {
  loading?: boolean
  interactive?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, loading, interactive, ...props }, ref) => {
    const finalVariant = loading ? "loading" : interactive ? "interactive" : variant;

    return (
      <div
        ref={ref}
        className={cn(cardVariants({ variant: finalVariant, size }), className)}
        {...props}
      />
    )
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-2 pb-4", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-xl font-semibold leading-tight tracking-tight text-foreground",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm leading-relaxed text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("space-y-4", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center justify-between pt-4", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
